import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useCategories } from '../context/CategoriesContext';
import { useSettings } from '../context/SettingsContext';
import { resolveImageUrl } from './ImageUpload';
import FeaturedSlideshow from './FeaturedSlideshow';
import HeroBestsellers from './HeroBestsellers';
import { visibilityClass } from '../lib/visibility';
import Rial from './Rial';

// Feb 2026 — helper that swaps the legacy "OMR" text prefix (or
// bare "OMR" label) inside admin-editable settings strings for the
// inline <Rial /> mark. Used for `settings.featured_price` +
// `stat.value` where the value ships from the DB as free-form text.
const swapOmr = (s) => {
  const str = String(s ?? '').trim();
  if (!str) return str;
  if (/^OMR\s+/.test(str)) {
    return (<><Rial /> {str.replace(/^OMR\s+/, '')}</>);
  }
  if (str === 'OMR') return <Rial />;
  return str;
};
import { resolveLayoutConfig } from '../lib/layout';

const Hero = () => {
  const { categories } = useCategories();
  const { settings } = useSettings();

  if (!settings) return null;

  // Resolve featured image: explicit image > linked category image > any category image
  const linkedCategory = settings.featured_link?.startsWith('/category/')
    ? categories.find((c) => c.slug === settings.featured_link.replace('/category/', ''))
    : null;
  const featuredImage =
    resolveImageUrl(settings.featured_image) ||
    resolveImageUrl(linkedCategory?.image) ||
    resolveImageUrl(categories[categories.length - 1]?.image);
  const featuredBg = settings.featured_bg || linkedCategory?.bg || '#E0F2FE';
  const featuredColor = settings.featured_color || linkedCategory?.color || '#0EA5E9';

  const isInternal = (href) => href && href.startsWith('/');
  const PrimaryCta = isInternal(settings.hero_primary_cta_link) ? Link : 'a';
  const SecondaryCta = isInternal(settings.hero_secondary_cta_link) ? Link : 'a';
  const FeaturedWrap = isInternal(settings.featured_link) ? Link : 'a';

  // When ticker mode is on, the slideshow moves OUT of the hero to a full-width band below
  const featuredCardEnabled =
    settings.show_featured_card !== false && settings.featured_display_mode !== 'ticker';

  // Admin can swap what sits in the right column. `featured` keeps the
  // legacy slideshow/card, `bestsellers` swaps in the compact bestseller
  // slider, `empty` collapses to a single-column hero.
  // Note: we read the desktop value here; CSS-in-JS via responsive media
  // queries would be cleaner but for now desktop drives the React render
  // and the column gets hidden via Tailwind `lg:` classes on mobile.
  const layoutCfg = resolveLayoutConfig(settings.layout);
  const rightDesktop = layoutCfg.hero_right_content.desktop;
  const rightMobile = layoutCfg.hero_right_content.mobile;

  // The right column renders unless BOTH devices say "empty". If only one
  // device says "empty" we still render and hide via Tailwind below.
  const anyRightContent =
    (rightDesktop !== 'empty' && featuredCardEnabled) ||
    (rightDesktop === 'bestsellers') ||
    (rightMobile !== 'empty' && featuredCardEnabled) ||
    (rightMobile === 'bestsellers');

  const showRightCard = anyRightContent;

  return (
    <section className="relative overflow-hidden bg-white">
      {/* Soft conic backdrop */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] conic-bg opacity-50" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12 lg:pt-16 pb-10 lg:pb-16">
        {/* Top eyebrow */}
        <div className={visibilityClass(settings, 'show_hero_eyebrow')}>
          <div className="flex items-center justify-center gap-2 mb-6 reveal">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 shadow-sm text-[11px] font-semibold tracking-wide text-slate-700">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-ring" />
              {settings.live_pill || 'B2B trade portal · live'}
            </span>
          </div>
        </div>

        <div className={`layout-hero-grid grid ${showRightCard ? 'lg:grid-cols-12' : 'lg:grid-cols-1'} gap-6 lg:gap-10 items-stretch`}>
          {/* Left: B2B value prop */}
          <div className={`layout-hero-text-col ${showRightCard ? 'lg:col-span-7' : ''} relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#0078D4] to-[#2563EB] p-7 sm:p-10 lg:p-14 flex flex-col justify-center text-white reveal`}>
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
            <div className="absolute -left-16 bottom-0 w-60 h-60 rounded-full bg-cyan-300/20 blur-3xl" />

            <div className="layout-hero-text relative" data-align={settings.layout?.elements?.hero?.alignment?.desktop || undefined}>
              <h1 className="font-display text-[2.5rem] sm:text-5xl lg:text-6xl xl:text-7xl leading-[0.98] text-white">
                {settings.hero_title_lead} <span className="text-cyan-200">{settings.hero_title_highlight}</span>
              </h1>
              <p className="mt-5 text-[15px] sm:text-base text-blue-100 max-w-md whitespace-pre-line">
                {settings.hero_subtitle}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <PrimaryCta
                  to={isInternal(settings.hero_primary_cta_link) ? settings.hero_primary_cta_link : undefined}
                  href={!isInternal(settings.hero_primary_cta_link) ? settings.hero_primary_cta_link : undefined}
                >
                  <Button className="h-12 px-6 rounded-full bg-white hover:bg-slate-100 text-[#0078D4] font-semibold text-[15px] btn-lift">
                    {settings.hero_primary_cta_label} <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </PrimaryCta>
                {settings.hero_secondary_cta_label && (
                  <SecondaryCta
                    to={isInternal(settings.hero_secondary_cta_link) ? settings.hero_secondary_cta_link : undefined}
                    href={!isInternal(settings.hero_secondary_cta_link) ? settings.hero_secondary_cta_link : undefined}
                    className="text-[14px] font-medium text-white/90 hover:text-white underline-hover"
                  >
                    {settings.hero_secondary_cta_label}
                  </SecondaryCta>
                )}
              </div>
            </div>
          </div>

          {/* Right: featured product / bestsellers — based on admin Layout choice */}
          {showRightCard && (() => {
            // Pick the renderer. On mobile the user may want a different
            // choice than desktop — we render the desktop content and use
            // Tailwind to hide on `<lg` when mobile differs (mobile gets
            // its own render below via responsive classes).
            const renderRight = (content, deviceClass) => {
              if (content === 'bestsellers') {
                return (
                  <HeroBestsellers
                    key={`bs-${deviceClass}`}
                    className={`lg:col-span-5 ${deviceClass}`}
                  />
                );
              }
              if (content === 'featured' && featuredCardEnabled) {
                return settings.featured_slides?.length > 0 ? (
                  <FeaturedSlideshow
                    key={`fs-${deviceClass}`}
                    slides={settings.featured_slides}
                    autoplaySeconds={settings.featured_autoplay_seconds ?? 5}
                    transition={settings.featured_transition || 'kenburns'}
                    wrapperClassName={`layout-hero-image-col ${deviceClass}`}
                  />
                ) : (
                  <FeaturedWrap
                    key={`fw-${deviceClass}`}
                    to={isInternal(settings.featured_link) ? settings.featured_link : undefined}
                    href={!isInternal(settings.featured_link) ? settings.featured_link : undefined}
                    className={`layout-hero-image-col lg:col-span-5 group relative rounded-3xl overflow-hidden card-lift reveal reveal-delay-2 aspect-square bg-white border border-slate-100 ${deviceClass}`}
                  >
                    <div className="h-full aspect-square flex flex-col p-5 sm:p-7" style={{ background: `linear-gradient(160deg, ${featuredBg} 0%, #ffffff 65%)` }}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">
                            {settings.featured_eyebrow}
                          </div>
                          <div className="font-display text-xl sm:text-2xl text-slate-900 mt-1 leading-tight">
                            {settings.featured_title}
                          </div>
                        </div>
                        {settings.featured_badge && (
                          <div
                            className="text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full text-white whitespace-nowrap shrink-0"
                            style={{ background: featuredColor }}
                          >
                            {settings.featured_badge}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 flex items-center justify-center min-h-0 my-3 sm:my-4">
                        {featuredImage && (
                          <img
                            src={featuredImage}
                            alt={settings.featured_title}
                            className="max-h-[200px] sm:max-h-[260px] lg:max-h-[280px] w-auto object-contain drop-shadow-2xl group-hover:scale-105 group-hover:-rotate-2 transition-transform duration-700 floaty"
                          />
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 shrink-0">
                        <div className="rounded-xl bg-white/70 backdrop-blur p-3 min-w-0">
                          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{settings.featured_price_label}</div>
                          <div className="text-lg font-bold text-slate-900 truncate">{swapOmr(settings.featured_price)}</div>
                        </div>
                        <div className="rounded-xl bg-white/70 backdrop-blur p-3 flex items-center justify-center gap-1.5 min-w-0">
                          <ArrowRight className="w-4 h-4 shrink-0" style={{ color: featuredColor }} />
                          <span className="text-sm font-semibold truncate" style={{ color: featuredColor }}>{settings.featured_cta_label}</span>
                        </div>
                      </div>
                    </div>
                  </FeaturedWrap>
                );
              }
              return null;
            };

            // If desktop and mobile agree, render once with no responsive
            // hiding classes. Otherwise render TWO siblings — one visible
            // on lg+, the other visible on <lg — so each device shows its
            // own choice without duplicate DOM weight beyond product images.
            if (rightDesktop === rightMobile) {
              return renderRight(rightDesktop, '');
            }
            return (
              <>
                {renderRight(rightDesktop, 'hidden lg:flex')}
                {renderRight(rightMobile, 'flex lg:hidden')}
              </>
            );
          })()}
        </div>

        {/* Stat bar */}
        {settings.stats?.length > 0 && (
          <div className={visibilityClass(settings, 'show_stats')}>
            <div className="mt-6 lg:mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 reveal reveal-delay-3">
              {settings.stats.map((s, i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-100 p-4 lg:p-5 flex items-center gap-3">
                  <div className="text-2xl lg:text-3xl font-display font-bold text-[#0078D4]">{swapOmr(s.value)}</div>
                  <div className="text-xs lg:text-sm font-medium text-slate-600">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;
